import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ProtectedRouteProps, UserRole } from '../../types/index'
import { Box, CircularProgress, Typography, Button } from '@mui/material'
import { useAuth } from '../../hooks/useAuth'
import { MainLayout } from '../layout/MainLayout'
import HippaConsentGuard from '../consent/HippaConsentGuard'
import AgencyHippaConsentGuard from '../consent/AgencyHippaConsentGuard'
import PatientConsentGuard from '../consent/PatientConsentGuard'
import ProviderVerificationGuard from '../verification/ProviderVerificationGuard'
import AgencyVerificationGuard from '../verification/AgencyVerificationGuard'

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  redirectTo = '/login',
}) => {
  const location = useLocation()
  const { user, isAuthenticated, isLoading, hasRole, logout } = useAuth()

  const getRoleDisplayName = (role: UserRole | null) => {
    if (!role) return 'Portal'
    switch (role) {
      case UserRole.THERAPIST:
        return 'Provider'
      case UserRole.CLIENT:
        return 'Patient'
      case UserRole.ADMIN:
        return 'Administrator'
      case UserRole.CERTIFICATE_MANAGER:
        return 'Certification Manager'
      default:
        return role
    }
  }
  
  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    )
  }

  // Redirect to login if not authenticated
  // if (!isAuthenticated || !user) {
  //   return <Navigate to={redirectTo} state={{ from: location }} replace />
  // }

  // Check role-based access
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <MainLayout showSidebar={false}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          gap={3}
          p={3}
          textAlign="center"
        >
          <Typography variant="h4" color="error" fontWeight={600}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={400}>
            You do not have permission to access this page. Your current role is{' '}
            <strong>{getRoleDisplayName(user?.role)}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Required role(s):{' '}
            {Array.isArray(requiredRole)
              ? requiredRole.map(role => getRoleDisplayName(role)).join(', ')
              : getRoleDisplayName(requiredRole)}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => window.history.back()}>
              Go Back
            </Button>
            {/* <Button variant="outlined" onClick={() => logout()}>
              Switch Account
            </Button> */}
          </Box>
        </Box>
      </MainLayout>
    )
  }

  // Helper function to check if role matches
  const isTherapistRole =
    requiredRole === UserRole.THERAPIST ||
    (Array.isArray(requiredRole) &&
      requiredRole.includes(UserRole.THERAPIST)) ||
    (!requiredRole && user?.role === UserRole.THERAPIST)

  const isPatientRole =
    requiredRole === UserRole.CLIENT ||
    (Array.isArray(requiredRole) && requiredRole.includes(UserRole.CLIENT)) ||
    (!requiredRole && user?.role === UserRole.CLIENT)

  const isAgencyRole =
    requiredRole === UserRole.AGENCY ||
    (Array.isArray(requiredRole) && requiredRole.includes(UserRole.AGENCY)) ||
    (!requiredRole && user?.role === UserRole.AGENCY)

    console.log("isAgencyRole", isAgencyRole)

  // Wrap children in MainLayout for authenticated users
  // For therapists, wrap with HIPAA consent guard and provider verification guard
  // For agency, wrap with agency HIPAA consent guard
  // For patients, wrap with intake consent guard
  let content = children

  if (isTherapistRole) {
    content = (
      <HippaConsentGuard>
        <ProviderVerificationGuard>{children}</ProviderVerificationGuard>
      </HippaConsentGuard>
    )
  } else if (isAgencyRole) {
    content = (
      <AgencyHippaConsentGuard>
        <AgencyVerificationGuard>{children}</AgencyVerificationGuard>
      </AgencyHippaConsentGuard>
    )
  } else if (isPatientRole) {
    content = <PatientConsentGuard>{children}</PatientConsentGuard>
  }

  return <MainLayout>{content}</MainLayout>
}

export default ProtectedRoute
